// src/pages/purchaseOrder/components/PurchaseOrderItemsSection.jsx
import React, { useState, useRef } from 'react';
import { Search, X, Package, Trash2, AlertCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getProducts } from '../../../services/api/productSearch';
import { getImageUrl, handleImageError } from '../../../utils/imageUtils';
import ProductThumb from '../../procurement/components/ProductThumb';
import { formatCurrency } from '../../../utils/currencyUtil';
import FieldError from '../../message-center/components/FieldError';
import QuantityStepper from '../../procurement/components/QuantityStepper';
import styles from './PurchaseOrderItemsSection.module.css';

const PurchaseOrderItemsSection = ({ items, errors, onAddItem, onUpdateItem, onRemoveItem, disabled = false, currency = null }) => {
  const { t } = useLocalization();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);

  const handleProductSearch = async (value) => {
    setSearchTerm(value);
    
    if (!value.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setIsSearching(true);
      const response = await getProducts({ 
        search: value, 
        limit: 10
      });
      
      const flatProducts = response.products.filter(product => product.type !== 'bundle').map(product => ({
        productId: product.productId,
        variationId: product.variationId,
        displayName: product.displayName,
        sku: product.sku,
        price: product.costPrice ?? '',
        image: product.image,
        type: product.type,
        weight: product.weight,
        stockQuantity: product.stockQuantity
      }));
      
      setSearchResults(flatProducts);
      setShowResults(true);
    } catch (error) {
      console.error('Product search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleProductSelect = (product) => {
    onAddItem(product);
    setSearchTerm('');
    setShowResults(false);
    searchRef.current?.focus();
  };

  return (
    <div className={styles.itemsContent}>
      {!disabled && (
        <div className={styles.productSearch}>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} size={18} />
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={(e) => handleProductSearch(e.target.value)}
              onFocus={() => searchTerm && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              placeholder={t('purchaseOrder.searchProductsPlaceholder')}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={() => { setSearchTerm(''); setShowResults(false); }} className={styles.clearButton}>
                <X size={16} />
              </button>
            )}
          </div>

          {showResults && (
            <div className={styles.searchResults}>
              {isSearching ? (
                <div className={styles.searchLoading}>
                  <div className={styles.loadingSpinner}></div>
                  <span>{t('purchaseOrder.searchingProducts')}</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div className={styles.resultsList}>
                  {searchResults.map((product) => (
                    <div
                      key={`${product.productId}-${product.variationId || 'simple'}`}
                      className={styles.resultItem}
                      onClick={() => handleProductSelect(product)}
                    >
                      {product.image && (
                        <img 
                          src={getImageUrl(product.image)} 
                          alt={product.displayName} 
                          className={styles.productImage} 
                          onError={(e) => handleImageError(e)} 
                        />
                      )}
                      <div className={styles.productInfo}>
                        <div className={styles.productName}>{product.displayName}</div>
                        <div className={styles.productSku}>{t('purchaseOrder.productSku')}: {product.sku || t('common.notAvailable')}</div>
                      </div>
                      <div className={styles.productPrice}>{formatCurrency(product.price, currency)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.noResults}>
                  <AlertCircle size={16} />
                  <span>{t('purchaseOrder.noProductsFound')}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {items.length > 0 ? (
        <div className={styles.itemsTable}>
          <table>
            <thead>
              <tr>
                <th>{t('purchaseOrder.itemNumber')}</th>
                <th>{t('purchaseOrder.itemProduct')}</th>
                <th>{t('purchaseOrder.itemQuantity')}</th>
                <th>{t('purchaseOrder.itemUnitPrice')}</th>
                <th>{t('purchaseOrder.itemLineTotal')}</th>
                {!disabled && <th>{t('purchaseOrder.itemActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    <div className={styles.itemProductRow}>
                      <ProductThumb src={item.imageUrl || null} alt={item.displayName} />
                      <div className={styles.itemProduct}>
                        <div className={styles.itemName}>{item.displayName}</div>
                        <div className={styles.itemSku}>{t('purchaseOrder.productSku')}: {item.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.errorCell}>
                    <QuantityStepper
                      value={item.quantity}
                      onChange={(value) => onUpdateItem(item.id, 'quantity', value)}
                      min={1}
                      disabled={disabled}
                      hasError={!!errors?.itemDetails?.[index]?.quantity}
                    />
                    <FieldError message={errors?.itemDetails?.[index]?.quantity} floating />
                    </div>
                  </td>
                  <td>
                    <div className={styles.errorCell}>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => onUpdateItem(item.id, 'unitPrice', e.target.value)}
                      min="0"
                      step="0.01"
                      className={`${styles.priceInput} ${errors?.itemDetails?.[index]?.unitPrice ? styles.inputError : ''}`}
                      disabled={disabled}
                    />
                    <FieldError message={errors?.itemDetails?.[index]?.unitPrice} floating />
                    </div>
                  </td>
                  <td className={styles.lineTotal}>
                    {formatCurrency(item.lineTotal, currency)}
                  </td>
                  {!disabled && (
                    <td>
                      <button
                        type="button"
                        onClick={() => onRemoveItem(item.id)}
                        className={styles.removeButton}
                        title={t('purchaseOrder.removeItem')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Package size={48} />
          <h3>{t('purchaseOrder.noItemsAdded')}</h3>
          <p>{t('purchaseOrder.addItemsToOrder')}</p>
        </div>
      )}

      {errors.items && (
        <div className={styles.errorMessage}>
          <AlertCircle size={16} />
          {errors.items}
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderItemsSection;